import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Edit2, MapPin, Users, Star, Bed, Bath, BarChart2, Calendar } from 'lucide-react';
import { propertyAPI, bookingAPI, getImageUrl } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import ICalSyncManager from '../../components/property/ICalSyncManager';
import PropertyCalendar from '../../components/calendar/PropertyCalendar';
import toast from 'react-hot-toast';

const PropertyDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canManage } = useAuth();
  const [property, setProperty] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [propRes, analyticsRes, bookingsRes] = await Promise.all([
          propertyAPI.getOne(id),
          propertyAPI.getAnalytics(id),
          bookingAPI.getAll({ propertyId: id, limit: 10 })
        ]);
        setProperty(propRes.data.data);
        setAnalytics(analyticsRes.data.data);
        setBookings(bookingsRes.data.data || []);
      } catch {
        toast.error('Failed to load property');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) return <div className="loading-center"><div className="spinner spinner-lg" /></div>;
  if (!property) return <div className="empty-state"><h3>Property not found</h3></div>;

  const coverImage = property.images?.find(i => i.isCover) || property.images?.[0];
  const statusColors = { active: '#059669', inactive: '#6B7280', maintenance: '#F59E0B', draft: '#9CA3AF' };
  const statusBg = { active: '#ECFDF5', inactive: '#F3F4F6', maintenance: '#FFFBEB', draft: '#F9FAFB' };

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-ghost btn-icon" onClick={() => navigate('/properties')}><ArrowLeft size={20} /></button>
          <div>
            <h1 className="page-title">{property.title}</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              <MapPin size={13} color="#6B7280" />
              <span style={{ fontSize: 14, color: '#6B7280' }}>{property.address?.city}, {property.address?.country}</span>
              <span className="badge" style={{ color: statusColors[property.status], background: statusBg[property.status] }}>
                {property.status}
              </span>
            </div>
          </div>
        </div>
        {canManage && (
          <button className="btn btn-primary" onClick={() => navigate(`/properties/${id}/edit`)}>
            <Edit2 size={16} /> Edit Property
          </button>
        )}
      </div>

      {/* Cover image */}
      {coverImage && (
        <div style={{ height: 280, borderRadius: 12, overflow: 'hidden', marginBottom: 24 }}>
          <img src={getImageUrl(coverImage.url)} alt={property.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
      )}

      <div className="tabs">
        {['overview', 'calendar', 'bookings', 'analytics', 'calendar-sync', 'reviews'].map(t => (
          <button key={t} className={`tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)} style={{ textTransform: 'capitalize' }}>{t === 'calendar-sync' ? 'Calendar Sync' : t}</button>
        ))}
      </div>

      {tab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div className="card">
              <div className="card-header"><span className="card-title">Property Details</span></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
                {[
                  { icon: Bed, label: 'Bedrooms', value: property.bedrooms },
                  { icon: Bath, label: 'Bathrooms', value: property.bathrooms },
                  { icon: Users, label: 'Max Guests', value: property.maxGuests },
                ].map(({ icon: Icon, label, value }) => (
                  <div key={label} style={{ textAlign: 'center', padding: 16, background: '#F9FAFB', borderRadius: 8 }}>
                    <Icon size={20} color="#3B82F6" style={{ marginBottom: 6 }} />
                    <div style={{ fontSize: 20, fontWeight: 700 }}>{value}</div>
                    <div style={{ fontSize: 12, color: '#6B7280' }}>{label}</div>
                  </div>
                ))}
              </div>
              {property.description && <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.7 }}>{property.description}</p>}
            </div>

            {property.amenities?.length > 0 && (
              <div className="card">
                <div className="card-header"><span className="card-title">Amenities</span></div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {property.amenities.map(a => (
                    <span key={a} className="badge badge-blue">✓ {a}</span>
                  ))}
                </div>
              </div>
            )}

            {property.rules && (
              <div className="card">
                <div className="card-header"><span className="card-title">House Rules</span></div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {[
                    { label: 'Smoking', value: property.rules.smokingAllowed },
                    { label: 'Pets', value: property.rules.petsAllowed },
                    { label: 'Parties', value: property.rules.partiesAllowed },
                  ].map(({ label, value }) => (
                    <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 16 }}>{value ? '✅' : '❌'}</span>
                      <span style={{ fontSize: 14, color: '#374151' }}>{label} {value ? 'Allowed' : 'Not Allowed'}</span>
                    </div>
                  ))}
                  <div style={{ fontSize: 14, color: '#374151' }}>🕒 Check-in: {property.rules.checkInTime}</div>
                  <div style={{ fontSize: 14, color: '#374151' }}>🕒 Check-out: {property.rules.checkOutTime}</div>
                </div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="card">
              <div className="card-header"><span className="card-title">Pricing</span></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  ['Base Price', `$${property.pricing?.basePrice?.toLocaleString()}/night`],
                  ['Cleaning Fee', `$${property.pricing?.cleaningFee || 0}`],
                  ['Security Deposit', `$${property.pricing?.securityDeposit || 0}`],
                  ['Min Nights', property.pricing?.minimumNights],
                  ['Max Nights', property.pricing?.maximumNights],
                ].map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                    <span style={{ color: '#6B7280' }}>{k}</span>
                    <span style={{ fontWeight: 500 }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>

            {analytics && (
              <div className="card">
                <div className="card-header"><span className="card-title">Quick Stats</span></div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[
                    ['Total Bookings', analytics.totalBookings],
                    ['Total Revenue', `$${analytics.totalRevenue?.toLocaleString()}`],
                    ['Occupancy Rate', `${analytics.occupancyRate}%`],
                    ['Avg Rating', analytics.averageRating ? `⭐ ${analytics.averageRating}` : 'No reviews'],
                  ].map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                      <span style={{ color: '#6B7280' }}>{k}</span>
                      <span style={{ fontWeight: 600, color: '#111827' }}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'bookings' && (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr><th>Booking #</th><th>Guest</th><th>Check In</th><th>Check Out</th><th>Nights</th><th>Total</th><th>Status</th></tr>
              </thead>
              <tbody>
                {bookings.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: '#9CA3AF' }}>No bookings yet</td></tr>
                ) : bookings.map(b => (
                  <tr key={b._id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/bookings/${b._id}`)}>
                    <td style={{ fontWeight: 500 }}>{b.bookingNumber}</td>
                    <td>{b.guest?.firstName} {b.guest?.lastName}</td>
                    <td>{new Date(b.checkIn).toLocaleDateString()}</td>
                    <td>{new Date(b.checkOut).toLocaleDateString()}</td>
                    <td>{b.nights}</td>
                    <td style={{ fontWeight: 600 }}>${b.pricing?.total?.toLocaleString()}</td>
                    <td><span className={`badge ${b.status === 'confirmed' ? 'badge-green' : b.status === 'pending' ? 'badge-yellow' : b.status === 'cancelled' ? 'badge-red' : 'badge-gray'}`}>{b.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'calendar' && (
        <PropertyCalendar propertyId={id} canManage={canManage} />
      )}

      {tab === 'calendar-sync' && (
        <ICalSyncManager propertyId={id} />
      )}

      {tab === 'reviews' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {(property.reviews || []).length === 0 ? (
            <div className="empty-state"><Star size={48} /><h3>No reviews yet</h3></div>
          ) : property.reviews.map(r => (
            <div key={r._id} className="card card-sm">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div className="avatar">{r.reviewer?.firstName?.[0]}{r.reviewer?.lastName?.[0]}</div>
                  <div>
                    <div style={{ fontWeight: 500 }}>{r.reviewer?.firstName} {r.reviewer?.lastName}</div>
                    <div style={{ fontSize: 12, color: '#9CA3AF' }}>{new Date(r.createdAt).toLocaleDateString()}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 2 }}>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} size={14} color={i < r.ratings?.overall ? '#F59E0B' : '#E5E7EB'} fill={i < r.ratings?.overall ? '#F59E0B' : 'none'} />
                  ))}
                </div>
              </div>
              <p style={{ fontSize: 14, color: '#374151' }}>{r.comment}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PropertyDetailPage;
